/**
 * configRegistry.js
 *
 * Declarative UI schema for the governed config taxonomies. ONE generic CRUD
 * component (<ConfigRegistry>) reads these definitions and renders the full
 * list + inline editor + add/remove + ordering + draft→publish flow.
 *
 * VALIDATION IS NOT DEFINED HERE. The per-field rules, the badge-class
 * allowlist, the governed-keys set, and the diff are imported from the SINGLE
 * shared module `@shared/configSchema.js` (= api/src/lib/configSchema.js), which
 * the Azure Functions API imports too. There is no hand-copied rule on the
 * client any more — the same code runs both sides, so client feedback and the
 * server's authoritative check can never drift apart.
 *
 * This file owns only what is purely presentational:
 *   - section titles / descriptions / singular nouns
 *   - which editor control each field uses (`type`) and its layout
 *   - the `newItem` seed for "+ Add"
 *   - row summary / badge-field accessors
 *
 * Field `type` values map 1:1 to editor controls in ConfigRegistry.jsx:
 *   text · colour · number · bool · aliases · eventTypes
 */

import {
  CONFIG_SCHEMA,
  validateConfigValue,
  diffConfig,
  isGovernedKey as isGovernedSchemaKey,
} from '@shared/configSchema.js';

// ── The UI registry (presentation only) ─────────────────────────────────────────
export const REGISTRY = {
  severityCodes: {
    title: 'Severity Codes',
    singular: 'code',
    description: 'Escalation levels used across cases, filters, and reports.',
    idField: 'key',
    // The label/value coupling: editing the label drives the canonical value.
    syncValueFromLabel: true,
    newItem: (items) => ({
      key: `code-${Date.now()}`, label: 'New Code', value: 'New Code',
      description: '', cssClass: '', order: nextOrder(items), eventTypes: [],
    }),
    rowBadgeField: 'cssClass',
    fields: [
      { name: 'label',       label: 'Label / display name',      type: 'text',   editorWidth: 'flex', required: true },
      { name: 'description', label: 'Description (shown in form)', type: 'text',  editorWidth: 'flex' },
      { name: 'cssClass',    label: 'Badge colour',              type: 'colour' },
      { name: 'eventTypes',  label: 'Available for event types', type: 'eventTypes' },
    ],
    summary: (item) => item.description,
  },

  statusValues: {
    title: 'Status Values',
    singular: 'status',
    description: 'Workflow states shown on cases, filters, and the dashboard.',
    idField: 'key',
    syncValueFromLabel: true,
    newItem: (items) => ({
      key: `status-${Date.now()}`, label: 'New Status', value: 'New Status',
      cssClass: '', isActive: true, order: nextOrder(items), eventTypes: [], treatmentTypes: [],
    }),
    rowBadgeField: 'cssClass',
    fields: [
      { name: 'label',    label: 'Label',                     type: 'text',   editorWidth: 'flex', required: true },
      { name: 'isActive', label: 'Active (shown in filters)', type: 'bool',   boolLabel: 'Active' },
      { name: 'cssClass', label: 'Badge colour',              type: 'colour' },
      { name: 'eventTypes', label: 'Available for event types', type: 'eventTypes' },
      { name: 'treatmentTypes', label: 'Available for treatment types', type: 'treatmentTypes' },
    ],
    summary: (item) => (item.isActive ? 'Active' : 'Inactive — hidden from filters'),
  },

  treatmentTypes: {
    title: 'Treatment Types',
    singular: 'type',
    description: 'Department / treatment groups. Aliases determine badge matching from SharePoint field values.',
    idField: 'key',
    syncValueFromLabel: true,
    newItem: (items) => ({
      key: `tx-${Date.now()}`, label: 'New Type', value: 'New Type',
      description: '', aliases: [], cssClass: '', order: nextOrder(items), eventTypes: [],
    }),
    rowBadgeField: 'cssClass',
    fields: [
      { name: 'label',       label: 'Short label',  type: 'text',    editorWidth: 'flex', required: true },
      { name: 'description', label: 'Description',   type: 'text',    editorWidth: 'flex' },
      { name: 'aliases',     label: 'Aliases (comma-separated, uppercase)', type: 'aliases',
        hint: 'SP values that resolve to this type. Include the raw field value exactly as stored (e.g. INJECTABLE, BOTOX).' },
      { name: 'cssClass',    label: 'Badge colour', type: 'colour'  },
      { name: 'eventTypes',  label: 'Available for event types', type: 'eventTypes' },
    ],
    summary: (item) => `${item.description || ''}${item.aliases?.length ? ` · aliases: ${item.aliases.join(', ')}` : ''}`,
  },

  requirementTypes: {
    title: 'Requirement Types',
    singular: 'requirement',
    description: 'Document/evidence asks an admin can attach to a case. Staff satisfy a requirement by uploading the file. "Other (specify)" is always available on the case picker for one-off asks.',
    idField: 'key',
    newItem: (items) => ({
      key: `req-${Date.now()}`, label: 'New requirement', order: nextOrder(items),
    }),
    rowBadgeField: '',
    fields: [
      { name: 'label', label: 'Label', type: 'text', editorWidth: 'flex', required: true },
    ],
    summary: () => '',
  },
};

// Next order = max existing + 1 (so a new row sorts to the end deterministically).
const nextOrder = (items) =>
  (items || []).reduce((m, i) => Math.max(m, Number(i.order) || 0), 0) + 1;

export const isGovernedKey = isGovernedSchemaKey;

// ── Validation — delegates to the shared validator (no rules live here) ──────────
// Returns the same { ok, errors, byId } shape ConfigRegistry.jsx already expects.
export function validateItems(key, items) {
  if (!REGISTRY[key]) return { ok: true, errors: [], byId: {} };
  return validateConfigValue(key, items);
}

// ── Diff for the publish preview — adapts the shared diff to the UI shape ────────
// The shared diffConfig returns { added:[{id,label}], removed:[...],
// changed:[{id,label,fields:{field:{from,to}}}] }. ConfigRegistry.jsx renders
// added/removed as items with .key+.label and changed as { item, diffs:[] }, so
// we reshape here rather than fork the diff logic.
export function diffItems(key, oldItems, newItems) {
  const spec = REGISTRY[key];
  const idField = spec?.idField || 'key';
  const d = diffConfig(key, oldItems || [], newItems || []);

  // Non-list / unknown key fallback.
  if (!d.added) {
    return { added: [], removed: [], changed: [], hasChanges: false };
  }

  const shape = (e) => ({ [idField]: e.id, key: e.id, label: e.label });
  const added = d.added.map(shape);
  const removed = d.removed.map(shape);
  const changed = d.changed.map(c => ({
    item: { [idField]: c.id, key: c.id, label: c.label },
    diffs: Object.entries(c.fields).map(([field, { from, to }]) => ({ field, from, to })),
  }));

  return {
    added, removed, changed,
    hasChanges: added.length + removed.length + changed.length > 0,
  };
}

export const fmtVal = (v) =>
  Array.isArray(v) ? (v.length ? v.join(', ') : '—')
  : typeof v === 'boolean' ? (v ? 'yes' : 'no')
  : v === '' || v == null ? '—'
  : String(v);

// Re-export the shared schema in case a caller wants the raw field/rule defs.
export { CONFIG_SCHEMA };
